<template>
  <v-card>
    <v-container fluid :style="{ whiteSpace: 'pre-wrap' }">
      <v-row dense align="center" justify="end">
        <v-col class="font-weight-bold">
          Receita de óculos {{ index + 1 }}
        </v-col>

        <v-col cols="auto">
          <authorization
            permissionKey="attendance_glasses_prescription"
            can="print"
            v-slot:default="{ authorization }"
          >
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  small
                  icon
                  :attrs="attrs"
                  v-on="on"
                  :disabled="false"
                  :loading="printLoading"
                  @click="downloadGlassesPrescription"
                  v-if="authorization || forcePrint"
                >
                  <v-icon>eva-printer-outline</v-icon>
                </v-btn>
              </template>
              <span> Imprimir </span>
            </v-tooltip>
          </authorization>

          <authorization
            v-if="allowEdit"
            permissionKey="attendance_glasses_prescription"
            can="save"
            v-slot:default="{ authorization }"
          >
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  small
                  icon
                  :attrs="attrs"
                  v-on="on"
                  :to="`receitas-oculos/${glassesPrescription.id}`"
                  v-if="authorization"
                  :disabled="signed"
                >
                  <v-icon>eva-edit-outline</v-icon>
                </v-btn>
              </template>

              <span> Editar </span>
            </v-tooltip>
          </authorization>

          <authorization
            v-if="allowDelete"
            permissionKey="attendance_glasses_prescription"
            can="delete"
            v-slot:default="{ authorization }"
          >
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  small
                  icon
                  :attrs="attrs"
                  v-on="on"
                  @click="deleteGlassesPrescription(glassesPrescription)"
                  v-if="authorization"
                  :disabled="signed"
                >
                  <v-icon>eva-trash-outline</v-icon>
                </v-btn>
              </template>

              <span> Excluir </span>
            </v-tooltip>
          </authorization>

          <v-btn icon small @click="showContent = !showContent">
            <v-icon v-if="showContent">eva-chevron-up-outline</v-icon>
            <v-icon v-else>eva-chevron-down-outline</v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <v-row dense v-show="showContent">
        <v-col>
          <v-divider />

          <v-row>
            <v-col cols="6" v-if="glassesPrescription.hasDistanceGlasses">
              <GlassesPrescriptionDistanceGlasses
                :isActive="glassesPrescription.hasDistanceGlasses"
                :distanceGlasses="glassesPrescription.distanceGlasses"
              />
            </v-col>

            <v-col cols="6">
              <GlassesPrescriptionReadingGlasses
                :isActive="glassesPrescription.hasReadingGlasses"
                :readingGlasses="glassesPrescription.readingGlasses"
              />
            </v-col>
          </v-row>

          <v-row v-if="glassesPrescription.pupillaryDistance">
            <v-col cols="auto">
              <GlassesPrescriptionPupillaryDistance
                :pupillaryDistance="glassesPrescription.pupillaryDistance"
              />
            </v-col>

            <v-col>
              <v-row>
                <v-col class="text-subtitle-2 gray--text text--lighten-1">
                  Próxima consulta
                </v-col>
              </v-row>

              <v-row dense>
                <v-col>
                  {{
                    $moment(
                      glassesPrescription.nextAttendanceDate,
                      "YYYY-MM-DD HH:mm:ss ZZ"
                    ) | moment("MM/YYYY")
                  }}
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col>
              <v-row dense>
                <v-col class="text-subtitle-2 gray--text text--lighten-1">
                  Obsevações
                </v-col>
              </v-row>

              <v-row dense>
                <v-col class="text">
                  {{ glassesPrescription.observations }}
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
  </v-card>
</template>

<script>
import GlassesPrescriptionDistanceGlasses from "@/components/glasses_prescription/distance_glasses.vue";
import GlassesPrescriptionReadingGlasses from "@/components/glasses_prescription/reading_glasses.vue";
import GlassesPrescriptionPupillaryDistance from "@/components/glasses_prescription/pupillary_distance.vue";

export default {
  name: "GlassPresciptionListItem",
  props: {
    glassesPrescription: Object,
    index: Number,
    refreshList: Function,
    allowEdit: {
      type: Boolean,
      default: true,
    },
    allowDelete: {
      type: Boolean,
      default: true,
    },
    forcePrint: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    GlassesPrescriptionDistanceGlasses,
    GlassesPrescriptionReadingGlasses,
    GlassesPrescriptionPupillaryDistance,
  },
  data() {
    return {
      printLoading: false,
      showContent: true,
    };
  },
  computed: {
    signed() {
      return this.glassesPrescription.lastEsign.status !== "editable";
    },
  },
  methods: {
    deleteGlassesPrescription() {
      if (this.signed) return;
      if (confirm("Você realmente deseja deletar essa receita de óculos?")) {
        this.apolloMutate({
          mutation: require("@/graphql/glasses_prescription/delete_glasses_prescription.gql"),
          variables: {
            glassesPrescriptionId: this.glassesPrescription.id,
          },
        }).then(() => {
          this.refreshList();
          this.$store.dispatch("createNotification", {
            message: "Receita de óculos deletada com sucesso.",
            type: "success",
          });
        });
      }
    },
    async generatePreview() {
      await this.apolloMutate({
        mutation: require("@/graphql/esign/generate_preview.gql"),
        variables: {
          esignId: this.glassesPrescription.lastEsign.id,
          dynamicForm: true,
          examAppraisal: true,
        },
      });
    },
    async downloadGlassesPrescription() {
      this.printLoading = true;
      await this.generatePreview();
      await this.refreshList();
      //await this.$store.dispatch("authentication/refreshToken");

      //if (this.glassesPrescription.lastEsign.status === "signed") {
      const file_url = this.glassesPrescription.lastEsign.fileUrl;

      await window.open(`${process.env.VUE_APP_BASE_URL}${file_url}`, "_blank");
      // } else {
      //   this.$store.dispatch("createNotification", {
      //     message: "É necessário assinar antes de imprimir.",
      //     type: "error",
      //   });
      //}

      this.printLoading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.text {
  white-space: pre-line;
}
</style>
